import { ButtonGroup, makeStyles, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import cn from 'classnames';
import { parse as parseQuery, stringify as stringifyQuery } from 'query-string';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _pick from 'lodash/pick';
import { FounderId } from '../../api/founders';
import { MentorId } from '../../api/mentors';
import { TenantId } from '../../api/tenants/Tenant';
import { Role } from '../../api/user/Role';
import venturesAPI from '../../api/ventures';
import { RouterContext } from '../../contexts/router-context';
import { UserContext } from '../../contexts/user-context';
import { useVenturesContext } from '../../contexts/ventures-context';
import { Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { decodeQuery, encodeQuery } from '../../utils/functions';
import { Link, Text } from '../common';
import List from '../common/list';
import VentureCard from '../list-cards/venture-card';

interface FilterOption {
  label: string;
  key: 'active' | 'attention' | 'nosessions';
}

interface FilterValue {
  type: string;
}

const useStyles = makeStyles(() => ({
  buttonGroup: {
    '& > button': {
      width: 100,
      padding: '4px 16px',
    },
  },
  disabledButtonGroup: {
    pointerEvents: 'none',
    opacity: 0.8,
    transition: 'opacity 0.3s ease-in-out',
  },
  centerBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    padding: 30,
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_45,
    textAlign: 'center',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 10,
    boxSizing: 'border-box',
  },
  emptyStateBtn: {
    marginTop: 16,
  },
}));

const filterOptions: FilterOption[] = [
  {
    label: 'Active',
    key: 'active',
  },
  {
    label: 'Attention',
    key: 'attention',
  },
  {
    label: 'No Meets',
    key: 'nosessions',
  },
];

export default function VenturesList() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user, identityid, hasRole, hasAccessToAction } =
    useContext(UserContext);
  const { ventures, setVentures } = useVenturesContext();
  const { prevPath } = useContext(RouterContext);

  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadError, setLoadError] = useState('');

  const prevVentureId = useMemo(() => {
    if (prevPath?.includes?.('/ventures/')) {
      const withoutQuery = prevPath.split('?')[0];
      const withoutHash = withoutQuery.split('#')[0];
      const splitPath = withoutHash.split('/');
      const venturesIndex = splitPath.indexOf('ventures');
      const ventureId = splitPath[venturesIndex + 1];

      if (ventureId && ventureId.length === 36) {
        return `card-${ventureId}`;
      }
    }

    if (prevPath?.includes('filter=')) {
      const encodedPrevFilter = prevPath.split('filter=')[1].split('&')[0];
      const prevFilter = decodeQuery(encodedPrevFilter) as any;
      if (prevFilter?.venture) {
        const ventureId = prevFilter.venture?.id;
        if (ventureId && ventureId.length === 36) {
          return `card-${ventureId}`;
        }
      }
    }
  }, [prevPath]);

  const searchParams = useMemo(
    () => parseQuery(location.search),
    [location.search],
  );

  const prepareNewURL = useCallback(
    (nextSearchParams) => {
      return `${location.pathname}${
        Object.keys(nextSearchParams).length
          ? `?${stringifyQuery(nextSearchParams)}`
          : ''
      }`;
    },
    [location.pathname],
  );

  const page = useMemo(() => {
    const parsedQuery = _pick(searchParams, ['page']);
    return parsedQuery.page ? Number(parsedQuery.page) : 0;
  }, [searchParams]);

  const selectedFilter = useMemo(() => {
    let selectedFilter;
    if (searchParams.filter) {
      const decodedFilter = decodeQuery(searchParams.filter as string);
      const parsedQuery = _pick(decodedFilter || {}, ['type']) as FilterValue;
      selectedFilter = filterOptions.find((o) => o.key === parsedQuery.type);
    }
    return selectedFilter || filterOptions[0];
  }, [searchParams.filter]);

  const handleFilterUpdate = useCallback(
    (key: string) => {
      const clearSearchParams = {
        ...searchParams,
        page: 0,
        filter: encodeQuery({
          type: key,
        }),
      };
      history.replace(prepareNewURL(clearSearchParams));
    },
    [history, prepareNewURL, searchParams],
  );

  const handleNextPage = useCallback(() => {
    if (!hasMorePages || isPageLoading) return;

    const nextSearchParams = {
      ...searchParams,
      page: page + 1,
    };
    history.replace(prepareNewURL(nextSearchParams));
  }, [hasMorePages, history, isPageLoading, page, prepareNewURL, searchParams]);

  const fetchVentures = useCallback(
    async ({
      filterKey,
      page = 0,
      userId,
      identityid,
    }: {
      filterKey?: FilterOption['key'];
      page?: number;
      userId: TenantId;
      identityid?: string;
    }) => {
      try {
        const defaultAdvancedFilters = {
          tenantId: userId,
          mentorsAssigned: -1,
          moreThanMentorsAssigned: -1,
          cohortId: null,
          status: null,
          mentorName: null,
          noSessions: false,
          withWizard: false,
          withIssues: false,
        };

        switch (filterKey) {
          case 'attention':
            return await venturesAPI.getVenturesAdvancedFilters(
              { ...defaultAdvancedFilters, withIssues: true },
              page,
            );
          case 'nosessions':
            return await venturesAPI.getVenturesAdvancedFilters(
              { ...defaultAdvancedFilters, noSessions: true, status: 'Active' },
              page,
            );
          case 'active':
          default:
            if (hasRole(Role.Mentor)) {
              return await venturesAPI.getVenturesDetailsByMentorId(
                identityid as MentorId,
              );
            } else if (hasRole(Role.Founder)) {
              return await venturesAPI.getVenturesDetailsByFounderId(
                identityid as FounderId,
              );
            } else {
              return await venturesAPI.getVenturesAdvancedFilters(
                { ...defaultAdvancedFilters, status: 'Active' },
                page,
              );
            }
        }
      } catch (e: any) {
        if (e?.response?.status === 404 || e?.message === '404') {
          setHasMorePages(false);
          return [];
        }

        console.error(e);
        const errorMessage =
          e?.response?.data?.message || 'Internal server error';
        setLoadError(errorMessage);
        return [];
      }
    },
    [hasRole],
  );

  useEffect(() => {
    const loadVentures = async () => {
      if (!user) {
        return;
      } else if (
        (hasRole(Role.Mentor) || hasRole(Role.Founder)) &&
        !identityid
      ) {
        return;
      }

      setIsLoading(true);
      setVentures([]);

      const pages = Array.from({ length: page + 1 }, (_, i) => i);
      const promises = pages.map((p) =>
        fetchVentures({
          page: p,
          userId: user.id,
          filterKey: selectedFilter.key,
          identityid,
        }),
      );

      const loadedVenturesResult = await Promise.all(promises);
      const loadedVentures = loadedVenturesResult.flat();

      setVentures(loadedVentures);
      setIsLoading(false);
    };

    loadVentures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchVentures, hasRole, identityid, selectedFilter.key, user]);

  useEffect(() => {
    const loadVenturesPage = async (page: number) => {
      if (!user || isLoading) {
        return;
      } else if (
        (hasRole(Role.Mentor) || hasRole(Role.Founder)) &&
        !identityid
      ) {
        return;
      }

      setIsPageLoading(true);

      const loadedVentures = await fetchVentures({
        page,
        userId: user.id,
        filterKey: selectedFilter.key,
        identityid,
      });

      setVentures((prev) => [...prev, ...loadedVentures]);
      setIsPageLoading(false);
    };

    loadVenturesPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <List
      prevId={prevVentureId}
      loadError={loadError}
      isLoading={isLoading}
      isPageLoading={isPageLoading}
      hasMorePages={hasMorePages}
      onNextPage={
        ventures.length && (hasRole(Role.Admin) || hasRole(Role.Manager))
          ? handleNextPage
          : undefined
      }
      filter={
        hasRole(Role.Admin) || hasRole(Role.Manager) ? (
          <ButtonGroup
            color='primary'
            aria-label='contained primary button group'
            className={cn(classes.buttonGroup, {
              [classes.disabledButtonGroup]: isLoading,
            })}>
            {filterOptions.map((option) => (
              <Button
                key={option.key}
                onClick={() => handleFilterUpdate(option.key)}
                variant={
                  selectedFilter.key === option.key ? 'contained' : 'outlined'
                }>
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        ) : null
      }>
      {!ventures.length ? (
        <div className={classes.centerBlock}>
          <Text variant='normal' testid='venture-table-no-items'>
            {!hasRole(Role.Admin) && !hasRole(Role.Manager)
              ? 'There are currently no active ventures assigned to you. It could be simply a work in progress. If this is unexpected, please reach out to your Program Administrator to clarify.'
              : `This panel contains a list of ventures filtered by ${selectedFilter.label}`}
            <br />
            {hasAccessToAction('venture.create') &&
              'We were not able to find information you requested, but feel free to add something new!'}
          </Text>
          {hasAccessToAction('venture.create') && (
            <Button
              component={Link}
              to={Pages.NEW_VENTURE}
              className={classes.emptyStateBtn}
              startIcon={<AddIcon />}
              variant='contained'
              color='primary'>
              Add Venture
            </Button>
          )}
        </div>
      ) : (
        <div className={classes.listContainer} key={selectedFilter.key}>
          {ventures.map((venture) => (
            <VentureCard
              key={venture.id}
              venture={venture}
              cardId={`card-${venture.id}`}
            />
          ))}
        </div>
      )}
    </List>
  );
}
